import { 
  Subscription, 
  SubscriptionPlan, 
  UsageStats, 
  BillingInfo, 
  PaymentHistory,
  SubscriptionApiResponse,
  UsageStatsApiResponse,
  PlanValidationResult,
  SubscriptionLimits,
  SUBSCRIPTION_PLAN_IDS,
  FeatureAccess
} from '../types/subscription';
import { LocalUsageStats } from './localUsageService';
import { SUBSCRIPTION_PLANS, getPlanById } from '../constants/subscriptionPlans';
import { db } from './firestore';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, deleteDoc, query, where, orderBy, limit } from 'firebase/firestore';

// Collection names
const USERS_COLLECTION = 'Users';
const SUBSCRIPTIONS_COLLECTION = 'Subscriptions';
const BILLING_INFO_SUBCOLLECTION = 'billingInfo';
const PAYMENT_HISTORY_COLLECTION = 'paymentHistory';

// Subscription Service Class
class SubscriptionService {
  // Get user's current subscription
  async getUserSubscription(userId: string): Promise<Subscription | null> {
    try {
      console.log('🔍 SubscriptionService: Starting getUserSubscription for userId:', userId);
      console.log('🔍 SubscriptionService: Current time:', new Date().toISOString());
      
      // First check user-specific subscription document (new structure)
      console.log('🔍 SubscriptionService: Checking user-specific subscription document...');
      const userSubscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, userId);
      console.log('🔍 SubscriptionService: User subscription document reference:', userSubscriptionRef.path);
      
      const userSubscriptionDoc = await getDoc(userSubscriptionRef);
      console.log('🔍 SubscriptionService: User subscription document exists:', userSubscriptionDoc.exists());
      
      if (userSubscriptionDoc.exists()) {
        const subscriptionData = userSubscriptionDoc.data();
        console.log('✅ SubscriptionService: Found subscription in user-specific document');
        console.log('📊 SubscriptionService: Subscription document data:', subscriptionData);
        
        const subscription = {
          id: userSubscriptionDoc.id,
          userId: userId,
          ...subscriptionData
        } as Subscription;
        
        console.log('📊 SubscriptionService: Final subscription object:', subscription);
        return subscription;
      } else {
        console.log('⚠️ SubscriptionService: No subscription found in user-specific document');
      }
      
      // Fallback: Check if subscription exists in user document (for backward compatibility)
      console.log('🔍 SubscriptionService: Checking user document for subscription (fallback)...');
      const userRef = doc(db, USERS_COLLECTION, userId);
      console.log('🔍 SubscriptionService: User document reference:', userRef.path);
      
      const userDoc = await getDoc(userRef);
      console.log('🔍 SubscriptionService: User document exists:', userDoc.exists());
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as any;
        console.log('🔍 SubscriptionService: User document data keys:', Object.keys(userData));
        console.log('🔍 SubscriptionService: User has subscription field:', !!userData.subscription);
        
        if (userData.subscription) {
          console.log('✅ SubscriptionService: Found subscription in user document (fallback)');
          console.log('📊 SubscriptionService: User document subscription:', userData.subscription);
          return userData.subscription as Subscription;
        } else {
          console.log('⚠️ SubscriptionService: User document exists but no subscription field');
        }
      } else {
        console.log('⚠️ SubscriptionService: User document does not exist');
      }
      
      console.log('⚠️ SubscriptionService: No subscription found in any location');
      return null;
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting user subscription:', error);
      console.error('❌ SubscriptionService: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        userId: userId
      });
      throw new Error('Failed to get subscription');
    }
  }

  // Create new subscription
  async createSubscription(subscription: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>): Promise<Subscription> {
    try {
      console.log('🔍 SubscriptionService: Creating subscription for user:', subscription.userId);
      
      // Create user-specific subscription document
      const userSubscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscription.userId);
      const now = Date.now().toString();
      
      const subscriptionData = {
        ...subscription,
        createdAt: now,
        updatedAt: now
      };
      
      // Remove undefined values before saving to Firestore
      const cleanSubscriptionData = Object.fromEntries(
        Object.entries(subscriptionData).filter(([_, value]) => value !== undefined)
      );
      
      console.log('🔍 SubscriptionService: Setting subscription document at:', userSubscriptionRef.path);
      console.log('📊 SubscriptionService: Subscription data:', cleanSubscriptionData);
      
      try {
        await setDoc(userSubscriptionRef, cleanSubscriptionData);
        console.log('✅ SubscriptionService: Successfully created subscription document in Firestore');
      } catch (setDocError) {
        console.error('❌ SubscriptionService: Error setting document:', setDocError);
        throw setDocError;
      }
      
      return {
        id: userSubscriptionRef.id,
        ...cleanSubscriptionData
      } as Subscription;
    } catch (error) {
      console.error('❌ SubscriptionService: Error creating subscription:', error);
      throw new Error('Failed to create subscription');
    }
  }

  // Update subscription
  async updateSubscription(userId: string, updates: Partial<Subscription>): Promise<void> {
    try {
      console.log('🔍 SubscriptionService: Updating subscription for user:', userId);
      
      const userSubscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, userId);
      const updateData = {
        ...updates,
        updatedAt: Date.now().toString()
      };
      
      console.log('🔍 SubscriptionService: Updating subscription document at:', userSubscriptionRef.path);
      console.log('📊 SubscriptionService: Update data:', updateData);
      
      await updateDoc(userSubscriptionRef, updateData);
      console.log('✅ SubscriptionService: Subscription updated successfully');
    } catch (error) {
      console.error('❌ SubscriptionService: Error updating subscription:', error);
      throw new Error('Failed to update subscription');
    }
  }

  // Cancel subscription
  async cancelSubscription(userId: string): Promise<void> {
    try {
      console.log('🔍 SubscriptionService: Cancelling subscription for user:', userId);
      
      const updates = {
        status: 'cancelled' as const,
        autoRenew: false,
        updatedAt: new Date().toISOString()
      };
      
      await this.updateSubscription(userId, updates);
      console.log('✅ SubscriptionService: Subscription cancelled successfully');
    } catch (error) {
      console.error('❌ SubscriptionService: Error cancelling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  }

  // Upgrade subscription plan
  async upgradePlan(userId: string, newPlanId: string): Promise<void> {
    try {
      console.log('🔍 SubscriptionService: Upgrading subscription for user:', userId, 'to plan:', newPlanId);
      
      const newPlan = getPlanById(newPlanId);
      if (!newPlan) {
        throw new Error('Plan not found');
      }

      const subscription = await this.getUserSubscription(userId);
      if (!subscription) {
        throw new Error('Subscription not found');
      }

      // Calculate proration (simplified)
      const prorationAmount = this.calculateProration(subscription, newPlan);

      const updates = {
        planId: newPlanId,
        totalPrice: newPlan.price,
        updatedAt: new Date().toISOString()
      };

      await this.updateSubscription(userId, updates);

      // TODO: Process payment for proration amount
      // await this.processPayment(subscription.userId, prorationAmount);
    } catch (error) {
      console.error('Error upgrading plan:', error);
      throw new Error('Failed to upgrade plan');
    }
  }

  // Downgrade subscription plan
  async downgradePlan(subscriptionId: string, newPlanId: string): Promise<void> {
    try {
      const newPlan = getPlanById(newPlanId);
      if (!newPlan) {
        throw new Error('Plan not found');
      }

      const updates = {
        planId: newPlanId,
        totalPrice: newPlan.price,
        updatedAt: new Date().toISOString()
      };

      await this.updateSubscription(subscriptionId, updates);
    } catch (error) {
      console.error('Error downgrading plan:', error);
      throw new Error('Failed to downgrade plan');
    }
  }

  // Get subscription by ID
  async getSubscriptionById(subscriptionId: string): Promise<Subscription | null> {
    try {
      const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (!subscriptionDoc.exists()) {
        return null;
      }
      
      return {
        id: subscriptionDoc.id,
        ...subscriptionDoc.data()
      } as Subscription;
    } catch (error) {
      console.error('Error getting subscription by ID:', error);
      throw new Error('Failed to get subscription');
    }
  }



  // Get subscription history for a user
  async getSubscriptionHistory(userId: string): Promise<Subscription[]> {
    try {
      console.log('🔍 SubscriptionService: Getting subscription history for user:', userId);
      
      // With the new structure, each user has only one subscription document
      // Return the current subscription as the "history"
      const currentSubscription = await this.getUserSubscription(userId);
      
      if (currentSubscription) {
        console.log('✅ SubscriptionService: Found current subscription for history');
        return [currentSubscription];
      } else {
        console.log('⚠️ SubscriptionService: No subscription found for history');
        return [];
      }
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting subscription history:', error);
      throw new Error('Failed to get subscription history');
    }
  }

  // Get all subscriptions (for admin)
  async getAllSubscriptions(): Promise<Subscription[]> {
    try {
      console.log('🔍 SubscriptionService: Getting all subscriptions (admin)');
      
      const subscriptionsRef = collection(db, SUBSCRIPTIONS_COLLECTION);
      const q = query(
        subscriptionsRef,
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const subscriptions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        userId: doc.id, // The document ID is the user ID in the new structure
        ...doc.data()
      } as Subscription));
      
      console.log('✅ SubscriptionService: Found', subscriptions.length, 'subscriptions');
      return subscriptions;
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting all subscriptions:', error);
      throw new Error('Failed to get subscriptions');
    }
  }

  // Create payment history record
  async createPaymentHistory(paymentData: Omit<PaymentHistory, 'id' | 'createdAt' | 'updatedAt'>): Promise<PaymentHistory> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const now = Date.now().toString();
      
      const paymentHistoryData = {
        ...paymentData,
        createdAt: now,
        updatedAt: now
      };
      
      // Remove undefined values before saving to Firestore
      const cleanPaymentData = Object.fromEntries(
        Object.entries(paymentHistoryData).filter(([_, value]) => value !== undefined)
      );
      
      const docRef = await addDoc(paymentHistoryRef, cleanPaymentData);
      
      return {
        id: docRef.id,
        ...cleanPaymentData
      } as PaymentHistory;
    } catch (error) {
      console.error('Error creating payment history:', error);
      throw new Error('Failed to create payment history');
    }
  }
      
  // Get payment history for a user
  async getPaymentHistory(userId: string): Promise<PaymentHistory[]> {
      try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
        const q = query(
        paymentHistoryRef,
          where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PaymentHistory));
    } catch (error) {
      console.error('Error getting payment history:', error);
      throw new Error('Failed to get payment history');
    }
  }

  // Get payment by transaction ID
  async getPaymentByTransactionId(transactionId: string): Promise<PaymentHistory | null> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const q = query(
        paymentHistoryRef,
        where('transactionId', '==', transactionId),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        
      if (querySnapshot.empty) {
        return null;
      }
      
      const paymentDoc = querySnapshot.docs[0];
          return {
        id: paymentDoc.id,
        ...paymentDoc.data()
      } as PaymentHistory;
    } catch (error) {
      console.error('Error getting payment by transaction ID:', error);
      throw new Error('Failed to get payment');
    }
  }

  // Get all payments (for admin)
  async getAllPayments(): Promise<PaymentHistory[]> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const q = query(
        paymentHistoryRef,
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PaymentHistory));
    } catch (error) {
      console.error('Error getting all payments:', error);
      throw new Error('Failed to get payments');
    }
  }

  // Get payments by status (for admin)
  async getPaymentsByStatus(status: string): Promise<PaymentHistory[]> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const q = query(
        paymentHistoryRef,
        where('paymentStatus', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PaymentHistory));
    } catch (error) {
      console.error('Error getting payments by status:', error);
      throw new Error('Failed to get payments');
    }
  }

  // Get payments by date range (for admin)
  async getPaymentsByDateRange(startDate: string, endDate: string): Promise<PaymentHistory[]> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const q = query(
        paymentHistoryRef,
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PaymentHistory));
    } catch (error) {
      console.error('Error getting payments by date range:', error);
      throw new Error('Failed to get payments');
    }
  }

  // Validate plan limits
  async validatePlanLimits(planId: string, usage: LocalUsageStats): Promise<PlanValidationResult> {
    try {
      const plan = getPlanById(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      const limits: SubscriptionLimits = {
        maxOffices: plan.maxOffices,
        maxClientsTotal: plan.maxClientsTotal,
        maxFilesTotal: plan.maxFilesTotal,
        maxTeamMembers: plan.maxTeamMembers
      };

      const exceededLimits: string[] = [];
      
      if (limits.maxOffices !== -1 && usage.officesCount >= limits.maxOffices) {
        exceededLimits.push('offices');
      }
      if (limits.maxClientsTotal !== -1 && usage.clientsCount >= limits.maxClientsTotal) {
        exceededLimits.push('clients');
      }
      if (limits.maxFilesTotal !== -1 && usage.filesCount >= limits.maxFilesTotal) {
        exceededLimits.push('files');
      }

      return {
        isValid: exceededLimits.length === 0,
        canUpgrade: true,
        canDowngrade: true,
        limits,
        currentUsage: usage,
        exceededLimits,
        suggestedPlan: exceededLimits.length > 0 ? SUBSCRIPTION_PLAN_IDS.PROFESSIONAL : undefined
      };
    } catch (error) {
      console.error('Error validating plan limits:', error);
      throw new Error('Failed to validate plan limits');
    }
  }

  // Check feature access
  getFeatureAccess(planId: string): FeatureAccess {
    const plan = getPlanById(planId);
    if (!plan) {
      return {
        eventReminders: false,
        advancedStats: false,
        community: false,
        phoneSupport: false,
        teamManagement: false,
        exportExcel: false
      };
    }

    return {
      eventReminders: plan.includesEventReminders,
      advancedStats: plan.includesAdvancedStats,
      community: plan.includesCommunity,
      phoneSupport: plan.includesPhoneSupport,
      teamManagement: plan.teamMemberPrice !== undefined,
      exportExcel: plan.includesExportExcel
    };
  }

  // Get billing information
  async getBillingInfo(userId: string): Promise<BillingInfo | null> {
    try {
      const billingInfoRef = collection(db, BILLING_INFO_SUBCOLLECTION);
      const q = query(
        billingInfoRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const billingDoc = querySnapshot.docs[0];
      return {
        id: billingDoc.id,
        ...billingDoc.data()
      } as BillingInfo;
    } catch (error) {
      console.error('Error getting billing info:', error);
      throw new Error('Failed to get billing information');
    }
  }


  // Calculate proration amount
  private calculateProration(currentSubscription: Subscription, newPlan: SubscriptionPlan): number {
    // Simplified proration calculation
    // In a real implementation, this would be more complex
    const currentPlan = getPlanById(currentSubscription.planId);
    if (!currentPlan) return newPlan.price;

    const priceDifference = newPlan.price - currentPlan.price;
    const daysRemaining = this.getDaysRemaining(currentSubscription.endDate);
    const daysInMonth = 30; // Simplified

    return Math.round((priceDifference * daysRemaining) / daysInMonth);
  }

  // Get days remaining in subscription
  private getDaysRemaining(endDate: string): number {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Process payment (simulation)
  async processPayment(userId: string, amount: number, paymentMethod: string): Promise<boolean> {
    try {
      console.log(`Processing payment for user ${userId}: ${amount} EGP via ${paymentMethod}`);
      
      // Simulate payment processing with random success/failure
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 90% success rate for simulation
      const success = Math.random() > 0.1;
      
      if (!success) {
        throw new Error('Payment simulation failed');
      }
      
      console.log(`Payment processed successfully for user ${userId}`);
      return true;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw new Error('Payment processing failed');
    }
  }

  // Create payment record
  async createPaymentRecord(payment: Omit<PaymentHistory, 'id' | 'createdAt' | 'updatedAt'>): Promise<PaymentHistory> {
    try {
      const paymentHistoryRef = collection(db, PAYMENT_HISTORY_COLLECTION);
      const now = new Date().toISOString();
      
      const paymentData = {
        ...payment,
        createdAt: now,
        updatedAt: now
      };
      
      const docRef = await addDoc(paymentHistoryRef, paymentData);
      
      return {
        id: docRef.id,
        ...paymentData
      } as PaymentHistory;
    } catch (error) {
      console.error('Error creating payment record:', error);
      throw new Error('Failed to create payment record');
    }
  }

}

// Export singleton instance
export const subscriptionService = new SubscriptionService();

// Make subscription service available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).subscriptionService = subscriptionService;
  console.log('🔧 SubscriptionService available globally as window.subscriptionService for debugging');
}

export default subscriptionService;
